import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

/*
 * InfinityTimeline — Premium cinematic infinity animation with moment scenes.
 *
 * Uses the exact EverBook logo SVG path via getPointAtLength()
 * for brand-perfect curvature. Renders on <canvas> for 60fps.
 *
 * Layers (back to front):
 *  1. Ambient glow
 *  2. Path gradient stroke
 *  3. Static micro-moment images (circular, subtle)
 *  4. Memory nodes (milestone dots)
 *  5. Center crossover anchor ("Today")
 *  6. Category labels
 *  7. Orb trail
 *  8. Dynamic moment (near orb, periodic)
 *  9. Orb (primary motion)
 */

const GREEN      = '#D4A574';
const GREEN_MID  = '#C49560';
const GREEN_GLOW = '#E0B989';
const GREEN_LITE = '#F4ECE2';
const WHITE      = '#FFFFFF';

/* Category labels — positioned at each loop apex along the new path
   traversal order (leftTip→UL apex→center→LR apex→rightTip→UR apex→
   center→LL apex→leftTip), so quadrants match: Family UL, Travel LL,
   Wins UR, Moments LR. */
const LABELS = [
  { text: 'Family',  frac: 0.13 },
  { text: 'Wins',     frac: 0.63 },
  { text: 'Moments', frac: 0.37 },
  { text: 'Travel',  frac: 0.87 },
];

/* Milestone nodes (static life points) — one per quadrant, with the extra
   5th node placed in the lower-left (Travel) quadrant since that quadrant
   has no dynamic pop-in slot, to keep visual weight balanced. */
const NODES = [
  { frac: 0.08 },
  { frac: 0.48 },
  { frac: 0.72 },
  { frac: 0.78 },
  { frac: 0.92 },
];

/* Static micro-moment image positions — between milestone nodes, away from center */
const MOMENT_SCENES = [
  { frac: 0.04, url: 'https://images.unsplash.com/photo-1758599670001-1253d37908ba?w=100&h=100&fit=crop&q=60' },
  { frac: 0.30, url: 'https://images.unsplash.com/photo-1738750204786-4825780372bb?w=100&h=100&fit=crop&q=60' },
  { frac: 0.56, url: 'https://images.unsplash.com/photo-1738748707734-7cb1bc45900b?w=100&h=100&fit=crop&q=60' },
  { frac: 0.80, url: 'https://images.unsplash.com/photo-1764267703921-94e44d9e905b?w=100&h=100&fit=crop&q=60' },
];

/* Dynamic moment slots — staggered pop-ins (fade in → hold → fade out) */
const DYNAMIC_IMG_URLS = [
  'https://images.unsplash.com/photo-1511988617509-a57c8a288659?w=120&h=120&fit=crop&q=60', // friends (family)
  'https://images.unsplash.com/photo-1519741497674-611481863552?w=120&h=120&fit=crop&q=60', // couple (travel)
  'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=120&h=120&fit=crop&q=60', // smile (wins)
];

export default function InfinityTimeline({ className = '' }) {
  const canvasRef = useRef(null);
  const animRef   = useRef(null);
  // Ambient phrase overlay — fades in/out every ~7s
  const [phraseVisible, setPhraseVisible] = useState(false);
  useEffect(() => {
    // Start slightly delayed, then toggle on a 7s rhythm
    let killed = false;
    const showMs = 2200;  // visible duration
    const gapMs  = 4800;  // hidden duration
    const tick = () => {
      if (killed) return;
      setPhraseVisible(true);
      setTimeout(() => {
        if (killed) return;
        setPhraseVisible(false);
        setTimeout(tick, gapMs);
      }, showMs);
    };
    const initial = setTimeout(tick, 1800);
    return () => { killed = true; clearTimeout(initial); };
  }, []);
  const stateRef  = useRef({
    t: 0,
    lastTime: 0,
    pathPts: null,
    pathLen: 0,
    w: 0, h: 0,
    cx: 0, cy: 0,
    centerFrac: 0.5,
    nodePulse: { idx: -1, phase: 0, nextAt: 0 },
    // Moment scene images (loaded)
    momentImgs: [],
    dynamicImgs: [],
    imgsLoaded: false,
    // Dynamic moment slots — each fades in/holds/fades out on its own staggered clock
    dynSlots: [],
  });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const st = stateRef.current;

    /* ── Pre-load moment images ── */
    if (!st.imgsLoaded) {
      st.imgsLoaded = true;
      MOMENT_SCENES.forEach((scene, i) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => { st.momentImgs[i] = img; };
        img.src = scene.url;
      });
      DYNAMIC_IMG_URLS.forEach((url, i) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => { st.dynamicImgs[i] = img; };
        img.src = url;
      });
      // Initialize staggered dynamic moment slots: 4 slots, one per quadrant
      // (UL/LR/UR/LL) so no quadrant is left visually emptier than the
      // others; the 4th slot reuses an existing image (no new asset added).
      const LIFETIME_MS = 1800; // fade-in 0.25 + hold 0.5 + fade-out 0.25 of lifetime
      const DYN_OFFSETS = [0.18, 0.42, 0.68, 0.93]; // one per quadrant
      st.dynSlots = DYN_OFFSETS.map((pathOffset, idx) => ({
        imgIdx: idx % DYNAMIC_IMG_URLS.length,
        pathOffset,
        active: false,
        phase: 0,
        lifetime: LIFETIME_MS,
        nextAt: 1400 + idx * 2200, // staggered: 1.4s, 3.6s, 5.8s, 8.0s from start
      }));
    }

    /* ── Build path using offscreen SVG ── */
    const buildPath = (w, h) => {
      st.w = w;
      st.h = h;
      st.cx = w / 2;
      st.cy = h / 2;

      // Path geometry derived from the official EverBook mark
      // (frontend/public/brand/everbook-mark-official.png), measured directly
      // from its pixel silhouette: crossover at (50, 31.16) of a 100×62.33
      // normalized box, loop apexes at x=18.5/81.5, outer tips level with
      // the vertical center. Replaces the former generic lucide-icon path.
      const svgNS = 'http://www.w3.org/2000/svg';
      const svg = document.createElementNS(svgNS, 'svg');
      svg.setAttribute('viewBox', '0 0 100 62.33');
      const path = document.createElementNS(svgNS, 'path');
      path.setAttribute('d',
        'M0,31.16 C0,0 18.5,0 50,31.16 C81.5,62.33 100,62.33 100,31.16 ' +
        'C100,0 81.5,0 50,31.16 C18.5,62.33 0,62.33 0,31.16 Z'
      );
      svg.appendChild(path);
      svg.style.cssText = 'position:absolute;left:-9999px;width:0;height:0';
      document.body.appendChild(svg);

      const totalLen = path.getTotalLength();
      st.pathLen = totalLen;

      const padX = w * 0.08;
      const padY = h * 0.18;
      const usableW = w - padX * 2;
      const usableH = h - padY * 2;
      const scaleX = usableW / 100;
      const scaleY = usableH / 62.33;
      const offsetX = padX;
      const offsetY = padY;

      const SAMPLES = 1200;
      const pts = [];
      let cDist = Infinity;
      let cIdx = 0;
      for (let i = 0; i <= SAMPLES; i++) {
        const len = (i / SAMPLES) * totalLen;
        const p = path.getPointAtLength(len);
        const x = p.x * scaleX + offsetX;
        const y = p.y * scaleY + offsetY;
        pts.push({ x, y });
        const dx = x - st.cx;
        const dy = y - st.cy;
        const d = dx * dx + dy * dy;
        if (d < cDist) { cDist = d; cIdx = i; }
      }
      st.pathPts = pts;
      st.centerFrac = cIdx / SAMPLES;
      document.body.removeChild(svg);
    };

    const getPoint = (frac) => {
      const pts = st.pathPts;
      if (!pts) return { x: 0, y: 0 };
      const f = ((frac % 1) + 1) % 1;
      const idx = Math.round(f * (pts.length - 1));
      return pts[Math.min(idx, pts.length - 1)];
    };

    const centerDist = (frac) => {
      const cf = st.centerFrac;
      const d = Math.abs(frac - cf);
      return Math.min(d, 1 - d);
    };

    const orbSpeed = (frac, dt) => {
      const d = centerDist(frac);
      const proximity = Math.max(0, 1 - d / 0.08);
      const ease = 1 - proximity * proximity * 0.6;
      // ~8s full loop — smooth easing, gentle slow-down near center.
      return (dt / 8000) * ease;
    };

    /* ── Draw circular image helper ── */
    const drawCircularImg = (ctx, img, x, y, radius, alpha) => {
      if (!img || !img.complete) return;
      ctx.save();
      ctx.globalAlpha = alpha;
      // Soft shadow
      ctx.shadowColor = 'rgba(0,0,0,0.25)';
      ctx.shadowBlur = 6;
      ctx.shadowOffsetY = 2;
      // Clip to circle
      ctx.beginPath();
      ctx.arc(x, y, radius, 0, Math.PI * 2);
      ctx.clip();
      // Draw image centered and covering the circle
      const size = radius * 2;
      ctx.drawImage(img, x - radius, y - radius, size, size);
      ctx.restore();
      // Soft glow ring
      ctx.save();
      ctx.globalAlpha = alpha * 0.3;
      ctx.beginPath();
      ctx.arc(x, y, radius + 1.5, 0, Math.PI * 2);
      ctx.strokeStyle = `${GREEN_LITE}60`;
      ctx.lineWidth = 1;
      ctx.stroke();
      ctx.restore();
    };

    /* ── Main render loop ── */
    const render = (timestamp) => {
      const ctx = canvas.getContext('2d');
      const dpr = window.devicePixelRatio || 1;
      const w = canvas.width / dpr;
      const h = canvas.height / dpr;

      if (!st.pathPts || st.w !== w) buildPath(w, h);

      const dt = st.lastTime ? timestamp - st.lastTime : 16;
      st.lastTime = timestamp;
      st.t = (st.t + orbSpeed(st.t, dt)) % 1;

      // Node pulse scheduling
      const np = st.nodePulse;
      if (timestamp > np.nextAt && np.idx === -1) {
        np.idx = Math.floor(Math.random() * NODES.length);
        np.phase = 0;
        np.nextAt = timestamp + 6000 + Math.random() * 4000;
      }
      if (np.idx >= 0) {
        np.phase += dt / 1200;
        if (np.phase >= 1) np.idx = -1;
      }

      // Dynamic moment slots — staggered fade in / hold / fade out cycles.
      // Each slot acts independently so the 3 pop-ins never bloom at once.
      st.dynSlots.forEach((slot) => {
        if (!slot.active && timestamp > slot.nextAt) {
          slot.active = true;
          slot.phase = 0;
        }
        if (slot.active) {
          slot.phase += dt / slot.lifetime;
          if (slot.phase >= 1) {
            slot.active = false;
            // Re-appear every 7.5–9.5s with per-slot offset to keep stagger.
            slot.nextAt = timestamp + 7500 + Math.random() * 2000;
          }
        }
      });

      ctx.save();
      ctx.scale(dpr, dpr);
      ctx.clearRect(0, 0, w, h);

      const pts = st.pathPts;
      if (!pts) { ctx.restore(); animRef.current = requestAnimationFrame(render); return; }

      const { cx, cy } = st;
      // Responsive moment size
      const momentR = Math.max(10, Math.min(w * 0.018, 14));

      /* ═══════════════════════════════
         LAYER 1: Ambient glow
         ═══════════════════════════════ */
      ctx.save();
      ctx.shadowColor = GREEN_GLOW;
      ctx.shadowBlur = 40;
      ctx.globalAlpha = 0.12;
      ctx.beginPath();
      pts.forEach((p, i) => i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y));
      ctx.strokeStyle = GREEN_GLOW;
      ctx.lineWidth = 6;
      ctx.stroke();
      ctx.restore();

      /* ═══════════════════════════════
         LAYER 2: Path gradient stroke
         ═══════════════════════════════ */
      const pathGrad = ctx.createLinearGradient(0, 0, w, 0);
      pathGrad.addColorStop(0, `${GREEN_MID}35`);
      pathGrad.addColorStop(0.5, `${GREEN}50`);
      pathGrad.addColorStop(1, `${GREEN_MID}35`);

      ctx.save();
      ctx.shadowColor = GREEN_GLOW;
      ctx.shadowBlur = 14;
      ctx.beginPath();
      pts.forEach((p, i) => i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y));
      ctx.strokeStyle = pathGrad;
      ctx.lineWidth = 2.5;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.stroke();
      ctx.restore();

      ctx.beginPath();
      pts.forEach((p, i) => i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y));
      ctx.strokeStyle = `${GREEN}18`;
      ctx.lineWidth = 1;
      ctx.stroke();

      /* ═══════════════════════════════
         LAYER 3: Static micro-moment images
         ═══════════════════════════════ */
      MOMENT_SCENES.forEach((scene, i) => {
        const img = st.momentImgs[i];
        if (!img) return;
        const p = getPoint(scene.frac);
        drawCircularImg(ctx, img, p.x, p.y, momentR, 0.92);
      });

      /* ═══════════════════════════════
         LAYER 4: Milestone nodes
         ═══════════════════════════════ */
      NODES.forEach((node, ni) => {
        const p = getPoint(node.frac);
        const isPulsing = np.idx === ni;
        const pulseT = isPulsing ? Math.sin(np.phase * Math.PI) : 0;
        const baseR = 3;
        const r = baseR + pulseT * 3;
        const glowR = 8 + pulseT * 12;

        const ng = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, glowR);
        ng.addColorStop(0, `${GREEN_LITE}${isPulsing ? '55' : '28'}`);
        ng.addColorStop(1, `${GREEN_LITE}00`);
        ctx.beginPath();
        ctx.arc(p.x, p.y, glowR, 0, Math.PI * 2);
        ctx.fillStyle = ng;
        ctx.fill();

        ctx.beginPath();
        ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
        const dotG = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, r);
        dotG.addColorStop(0, isPulsing ? `${WHITE}CC` : `${GREEN_LITE}60`);
        dotG.addColorStop(1, isPulsing ? `${GREEN_LITE}80` : `${GREEN_LITE}30`);
        ctx.fillStyle = dotG;
        ctx.fill();
      });

      /* ═══════════════════════════════
         LAYER 5: Center crossover anchor (halo + dot only;
         "Today" label is drawn last so it sits ABOVE all motion layers)
         ═══════════════════════════════ */
      const cgR = 36;
      const cg = ctx.createRadialGradient(cx, cy, 0, cx, cy, cgR);
      cg.addColorStop(0, `${GREEN_LITE}38`);
      cg.addColorStop(0.4, `${GREEN_GLOW}18`);
      cg.addColorStop(1, `${GREEN_GLOW}00`);
      ctx.beginPath();
      ctx.arc(cx, cy, cgR, 0, Math.PI * 2);
      ctx.fillStyle = cg;
      ctx.fill();

      ctx.beginPath();
      ctx.arc(cx, cy, 4, 0, Math.PI * 2);
      const cdg = ctx.createRadialGradient(cx, cy, 0, cx, cy, 4);
      cdg.addColorStop(0, `${WHITE}AA`);
      cdg.addColorStop(1, `${GREEN_LITE}70`);
      ctx.fillStyle = cdg;
      ctx.fill();

      /* ═══════════════════════════════
         LAYER 6: Category labels
         ═══════════════════════════════ */
      ctx.save();
      ctx.font = `400 10px -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif`;
      ctx.textAlign = 'center';
      ctx.fillStyle = `${GREEN}65`;
      LABELS.forEach(({ text, frac }) => {
        const p = getPoint(frac);
        const aboveCenter = p.y < cy;
        const dy = aboveCenter ? -18 : 22;
        ctx.fillText(text, p.x, p.y + dy);
      });
      ctx.restore();

      /* ═══════════════════════════════
         LAYER 7: Orb trail
         ═══════════════════════════════ */
      const trailFrac = 1.5 / 12;
      const trailSteps = 60;
      for (let i = trailSteps; i > 0; i--) {
        const stepFrac = (i / trailSteps) * trailFrac;
        const tf = (st.t - stepFrac + 1) % 1;
        const tp = getPoint(tf);
        const progress = (trailSteps - i) / trailSteps;
        const alpha = progress * progress * 0.4;
        const r = 1.5 + progress * 2.5;
        ctx.beginPath();
        ctx.arc(tp.x, tp.y, r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(212, 165, 116, ${alpha})`;
        ctx.fill();
      }

      /* ═══════════════════════════════
         LAYER 8: Dynamic moment pop-ins (staggered along the path)
         Each slot: fade in → hold ~40% → fade out.  Slots bloom at
         different path offsets and on different clocks so the viewer
         never sees more than ~2 at once.
         ═══════════════════════════════ */
      st.dynSlots.forEach((slot) => {
        if (!slot.active) return;
        const img = st.dynamicImgs[slot.imgIdx];
        if (!img) return;
        const phase = slot.phase;
        // Fade in (0→0.28), hold (0.28→0.72), fade out (0.72→1)
        let alpha;
        if (phase < 0.28)       alpha = phase / 0.28;
        else if (phase < 0.72)  alpha = 1;
        else                    alpha = 1 - (phase - 0.72) / 0.28;
        alpha *= 0.92;

        const dp = getPoint(slot.pathOffset);
        // Offset away from the path on its nearest side so image doesn't
        // sit on top of the path stroke.
        const aboveCenter = dp.y < cy;
        const offY = (aboveCenter ? -1 : 1) * momentR * 2.4;
        const scale = 0.94 + Math.sin(phase * Math.PI) * 0.08;
        drawCircularImg(ctx, img, dp.x, dp.y + offY, momentR * scale, alpha);
      });

      /* ═══════════════════════════════
         LAYER 9: Orb (primary motion)
         ═══════════════════════════════ */
      const orbPt = getPoint(st.t);
      const cProx = Math.max(0, 1 - centerDist(st.t) / 0.08);
      const orbScale = 1 + cProx * 0.4;
      const orbR = 5.5 * orbScale;

      let pulse = 0;
      if (cProx > 0.3) {
        pulse = Math.sin(timestamp * 0.008) * cProx * 0.15;
      }
      const finalR = orbR * (1 + pulse);

      const glR = finalR * (4 + cProx * 2);
      const og = ctx.createRadialGradient(orbPt.x, orbPt.y, 0, orbPt.x, orbPt.y, glR);
      const glowAlpha = 0.35 + cProx * 0.25;
      og.addColorStop(0, `rgba(91, 191, 138, ${glowAlpha})`);
      og.addColorStop(0.3, `rgba(212, 165, 116, ${glowAlpha * 0.4})`);
      og.addColorStop(1, 'rgba(212, 165, 116, 0)');
      ctx.beginPath();
      ctx.arc(orbPt.x, orbPt.y, glR, 0, Math.PI * 2);
      ctx.fillStyle = og;
      ctx.fill();

      ctx.beginPath();
      ctx.arc(orbPt.x, orbPt.y, finalR, 0, Math.PI * 2);
      const coreG = ctx.createRadialGradient(
        orbPt.x - finalR * 0.2, orbPt.y - finalR * 0.2, 0,
        orbPt.x, orbPt.y, finalR
      );
      coreG.addColorStop(0, WHITE);
      coreG.addColorStop(0.4, GREEN_LITE);
      coreG.addColorStop(1, GREEN_GLOW);
      ctx.fillStyle = coreG;
      ctx.fill();

      /* ═══════════════════════════════
         LAYER 10: "Today" label (top — above all motion)
         Offset above the crossover, soft halo pill, bolder weight.
         ═══════════════════════════════ */
      ctx.save();
      ctx.font = `600 11px -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif`;
      const labelText = 'Today';
      const labelM = ctx.measureText(labelText);
      const labelW = labelM.width;
      const labelH = 14;
      const labelY = cy - 26;   // ~14px above the dot + intersection lines
      const padX = 7;
      const padY = 3;
      // Soft halo pill
      const rx = labelW / 2 + padX;
      const ry = labelH / 2 + padY;
      const haloR = Math.max(rx, ry) + 8;
      const halo = ctx.createRadialGradient(cx, labelY, 0, cx, labelY, haloR);
      halo.addColorStop(0, 'rgba(247, 243, 236, 0.95)');
      halo.addColorStop(0.6, 'rgba(247, 243, 236, 0.55)');
      halo.addColorStop(1, 'rgba(247, 243, 236, 0)');
      ctx.fillStyle = halo;
      ctx.beginPath();
      ctx.arc(cx, labelY, haloR, 0, Math.PI * 2);
      ctx.fill();
      // Pill background
      ctx.beginPath();
      const left = cx - rx, top = labelY - ry;
      const r = ry;
      ctx.moveTo(left + r, top);
      ctx.lineTo(left + rx * 2 - r, top);
      ctx.quadraticCurveTo(left + rx * 2, top, left + rx * 2, top + r);
      ctx.lineTo(left + rx * 2, top + ry * 2 - r);
      ctx.quadraticCurveTo(left + rx * 2, top + ry * 2, left + rx * 2 - r, top + ry * 2);
      ctx.lineTo(left + r, top + ry * 2);
      ctx.quadraticCurveTo(left, top + ry * 2, left, top + ry * 2 - r);
      ctx.lineTo(left, top + r);
      ctx.quadraticCurveTo(left, top, left + r, top);
      ctx.closePath();
      ctx.fillStyle = 'rgba(255, 255, 255, 0.78)';
      ctx.fill();
      // Text
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = GREEN;
      ctx.fillText(labelText, cx, labelY + 0.5);
      ctx.restore();

      ctx.restore();
      animRef.current = requestAnimationFrame(render);
    };

    /* ── Resize handler ── */
    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.parentElement.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;
      st.pathPts = null;
      st.w = 0;
    };

    resize();
    window.addEventListener('resize', resize);
    animRef.current = requestAnimationFrame(render);

    return () => {
      window.removeEventListener('resize', resize);
      if (animRef.current) cancelAnimationFrame(animRef.current);
    };
  }, []);

  return (
    <div className={`relative w-full h-full min-h-[280px] ${className}`}>
      <canvas
        ref={canvasRef}
        className="w-full h-full"
        data-testid="infinity-timeline-canvas"
      />
      {/* Ambient phrase overlay — fades in/out on a gentle rhythm.
          Positioned BELOW the infinity center so it never fights with the
          headline, and well away from the "Today" chip. */}
      <div
        className="absolute inset-x-0 bottom-[14%] flex justify-center pointer-events-none"
        aria-hidden
      >
        <AnimatePresence>
          {phraseVisible && (
            <motion.span
              key="phrase"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 0.55, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="text-[13px] sm:text-[14px] tracking-wide italic"
              style={{
                color: GREEN,
                fontFamily: "'Cormorant Garamond', 'Playfair Display', Georgia, serif",
              }}
              data-testid="infinity-ambient-phrase"
            >
              This belongs in your EverBook.
            </motion.span>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
